import React, { Component, type ErrorInfo, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { CLINICAL_FONT_FAMILY } from "@/components/shared/constants";
import type { User } from "@/types/Sidebar.type";
import type { Study } from "@/pages/StudyManagement";
import { UserRoleEnum } from "@/types/role.type";
import { getPrimaryRole } from "./helpers";
import {
  Dashboard,
  PrincipalInvestigatorDashboard,
  StudyCoordinatorDashboard,
  DataManagerDashboard,
  SponsorDashboard,
  SiteAdminDashboard,
  SiteFeasibilityPage,
} from "./lazyPages";

/**
 * Keeps Header/Sidebar mounted when a page throws (e.g. partial API data).
 * Without this, a Tenant Dashboard crash blanks the whole authenticated shell
 * and menu clicks appear to do nothing.
 */
class RouteErrorBoundaryInner extends Component<
  { children: ReactNode; resetKey: string },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidUpdate(prevProps: { resetKey: string }) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    void error;
    void info;
  }

  render() {
    if (this.state.error) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "50vh",
            gap: 2,
            p: 3,
            fontFamily: CLINICAL_FONT_FAMILY,
          }}
        >
          <Typography variant="h6" sx={{ color: "#0F172A", fontWeight: 600 }}>
            This page failed to load
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "#64748B", textAlign: "center", maxWidth: 420 }}
          >
            You can keep using the sidebar to open another page, or retry.
          </Typography>
          <Button
            variant="contained"
            onClick={() => {
              this.setState({ error: null });
            }}
          >
            Retry
          </Button>
        </Box>
      );
    }
    return this.props.children;
  }
}

export const RouteErrorBoundary: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const location = useLocation();
  return (
    <RouteErrorBoundaryInner resetKey={location.pathname}>
      {children}
    </RouteErrorBoundaryInner>
  );
};

export const LoadingSpinner: React.FC = () => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "50vh",
      gap: 2,
    }}
  >
    <CircularProgress size={40} />
    <Typography
      variant="body2"
      sx={{
        color: "#64748B",
        fontFamily: CLINICAL_FONT_FAMILY,
      }}
    >
      Loading...
    </Typography>
  </Box>
);

export const NotFound: React.FC = () => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "50vh",
      gap: 2,
      p: 3,
    }}
  >
    <Typography
      variant="h2"
      sx={{
        color: "#0F172A",
        fontFamily: CLINICAL_FONT_FAMILY,
      }}
    >
      404
    </Typography>
    <Typography
      variant="body1"
      sx={{
        color: "#64748B",
        fontFamily: CLINICAL_FONT_FAMILY,
        textAlign: "center",
      }}
    >
      Page not found or you don't have permission to access this page.
    </Typography>
  </Box>
);

export const PlaceholderPage: React.FC<{ title: string }> = ({ title }) => (
  <Box sx={{ p: 3 }}>
    <Typography
      variant="h2"
      sx={{
        color: "#0F172A",
        fontFamily: CLINICAL_FONT_FAMILY,
        mb: 2,
      }}
    >
      {title}
    </Typography>
    <Typography
      variant="body1"
      sx={{
        color: "#64748B",
        fontFamily: CLINICAL_FONT_FAMILY,
      }}
    >
      This page is under development and will be available soon.
    </Typography>
  </Box>
);

/** Role-based home dashboard for `/dashboard`. */
export const RoleDashboard: React.FC<{
  user: User;
  onLogout: () => void;
  selectedStudyId?: string;
  userStudyRoles?: Record<string, string>;
  availableStudies?: Study[];
}> = ({
  user,
  onLogout,
  selectedStudyId,
  userStudyRoles,
  availableStudies = [],
}) => {
  const userRole = getPrimaryRole(user, selectedStudyId, userStudyRoles);
  const currentStudy = availableStudies.find((s) => s.id === selectedStudyId);

  switch (userRole) {
    case UserRoleEnum.TENANT:
      return <Dashboard onLogout={onLogout} />;
    case UserRoleEnum.PRINCIPAL_INVESTIGATOR:
      return <PrincipalInvestigatorDashboard onLogout={onLogout} />;
    case UserRoleEnum.STUDY_COORDINATOR:
      return <StudyCoordinatorDashboard onLogout={onLogout} />;
    case UserRoleEnum.CRA:
      return <Dashboard onLogout={onLogout} />;
    case UserRoleEnum.CTA:
      return (
        <SiteFeasibilityPage currentStudy={currentStudy} userRole={userRole} />
      );
    case UserRoleEnum.DATA_MANAGER:
      return <DataManagerDashboard onLogout={onLogout} />;
    case UserRoleEnum.SPONSOR:
      return (
        <SponsorDashboard currentStudy={currentStudy} userRole={userRole} />
      );
    case UserRoleEnum.SITE_ADMIN:
      return <SiteAdminDashboard onLogout={onLogout} />;
    case UserRoleEnum.QA_QC:
    case UserRoleEnum.ACCOUNTANT:
    case UserRoleEnum.FM:
    case UserRoleEnum.REGULATORY:
    default:
      return <Dashboard onLogout={onLogout} />;
  }
};
